import { Link } from 'react-router-dom'
import { Faq } from '../components/Faq'
import { useReveal } from '../components/marketing/motion'
import rawStats from '../data/carrier-stats.generated.json'
import { CARRIERS } from '../data/carriers'
import faqs from '../data/faqs.json'
import { type CarrierStatsLike, reliabilityFacts } from '../lib/carrier-reliability'
import { usePageMeta } from '../lib/usePageMeta'

const HUB_FAQ = faqs.carriers
const CARRIER_STATS = rawStats as Record<string, CarrierStatsLike | undefined>
const BUILD_DATE = typeof __BUILD_DATE__ !== 'undefined' ? __BUILD_DATE__ : ''
const num = (v: number | undefined) => (v == null ? '—' : v.toLocaleString('en-US'))

// Rank carriers by observed on-schedule departure share (most reliable first); thin/absent-sample
// carriers sink to the bottom, labelled low-sample — mirrors hubRankedCarriers() in prerender.carriers.mjs.
const RANKED = (() => {
  const rows = CARRIERS.map((c) => {
    const stats = CARRIER_STATS[c.slug]
    return { c, stats, facts: reliabilityFacts(stats) }
  })
  const withFacts = rows
    .filter((r) => r.facts)
    .sort((a, b) => (b.facts?.onSchedulePct ?? 0) - (a.facts?.onSchedulePct ?? 0))
  const withoutFacts = rows
    .filter((r) => !r.facts)
    .sort((a, b) => (b.stats?.sailings || 0) - (a.stats?.sailings || 0))
  return [...withFacts, ...withoutFacts]
})()

export function CarriersHubPage() {
  usePageMeta(
    `Ocean carrier sailing schedules: all ${CARRIERS.length} carriers · SchedulesMCP`,
    `Sailing schedules and real on-time reliability for ${CARRIERS.length} ocean carriers: MSC, Maersk, CMA CGM, COSCO, Hapag-Lloyd, ONE and more. Compare any carrier on your lane before you book.`
  )
  useReveal('main')
  return (
    <main
      className="mx-auto w-full px-6 pt-12 pb-16"
      style={{ maxWidth: 'min(1240px, 100vw - 48px)' }}
    >
      <nav className="text-sm text-[var(--text-muted)] mb-10">
        <Link to="/" className="no-underline hover:underline">
          SchedulesMCP
        </Link>{' '}
        › Carriers
      </nav>

      {/* Hero — book §07 anatomy: teal eyebrow → ultralight title → one-line hook → live lead */}
      <header className="text-center mb-14">
        <p
          className="m-0 font-mono text-xs uppercase text-accent"
          style={{ letterSpacing: '0.14em' }}
        >
          Carrier reliability
        </p>
        <h1
          className="mx-auto mt-10 mb-0 font-display text-[clamp(28px,4.6vw,48px)] leading-[1.08] text-[var(--text-primary)]"
          style={{ fontWeight: 210, letterSpacing: '-0.03em' }}
        >
          Ocean carrier sailing schedules, ranked
        </h1>
        <p className="mx-auto mt-10 mb-0 text-base leading-[1.5] text-[var(--text-secondary)]">
          Every carrier with a live connector, normalised into one shape and ranked by the
          departures they actually made.
        </p>
        <p className="mx-auto mt-6 mb-0 max-w-[62ch] text-sm leading-relaxed text-[var(--text-muted)] text-justify [hyphens:auto]">
          {(() => {
            const top = RANKED.find((r) => r.facts)
            const f = top?.facts
            return top && f
              ? `Across the ${CARRIERS.length} ocean carriers SchedulesMCP tracks, the most schedule-reliable over the last ${f.windowDays} days is ${top.c.name}, departing on schedule about ${f.onSchedulePct}% of the time across ${f.n.toLocaleString('en-US')} observed sailings. The full ranked comparison (sailings, lanes, on-schedule share and schedule drift) is below.`
              : `SchedulesMCP tracks ${CARRIERS.length} ocean carriers, each normalised into one comparable shape with observed schedule-reliability where the sample supports it. The ranked comparison is below.`
          })()}
        </p>
        <p className="mx-auto mt-3 text-xs text-[var(--text-muted)]">
          Schedule-reliability = observed departure punctuality over the trailing window (from
          carriers' own ETD revisions). Data as of {BUILD_DATE}.
        </p>
      </header>

      <div className="overflow-x-auto border border-border rounded-xl">
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr className="border-b border-border">
              <th className="text-left p-3 text-xs uppercase tracking-wide text-[var(--text-muted)]">
                Carrier
              </th>
              <th className="text-left p-3 text-xs uppercase tracking-wide text-[var(--text-muted)]">
                Sailings
              </th>
              <th className="text-left p-3 text-xs uppercase tracking-wide text-[var(--text-muted)]">
                Lanes
              </th>
              <th className="text-left p-3 text-xs uppercase tracking-wide text-[var(--text-muted)]">
                On-schedule departures
              </th>
              <th className="text-left p-3 text-xs uppercase tracking-wide text-[var(--text-muted)]">
                Behind schedule
              </th>
              <th className="text-left p-3 text-xs uppercase tracking-wide text-[var(--text-muted)]">
                p90 drift
              </th>
            </tr>
          </thead>
          <tbody>
            {RANKED.map(({ c, stats, facts }) => (
              <tr key={c.slug} className="border-b border-border">
                <td className="p-3">
                  <Link
                    to={`/carriers/${c.slug}`}
                    className="text-signal font-semibold no-underline hover:underline"
                  >
                    {c.name}
                  </Link>{' '}
                  <span className="text-[var(--text-muted)] text-xs">{c.scac}</span>
                </td>
                <td className="p-3 text-[var(--text-muted)] font-mono">{num(stats?.sailings)}</td>
                <td className="p-3 text-[var(--text-muted)] font-mono">{num(stats?.lanes)}</td>
                {facts ? (
                  <>
                    <td className="p-3 text-[var(--text-primary)] font-mono font-semibold">
                      {facts.onSchedulePct}%
                    </td>
                    <td className="p-3 text-[var(--text-muted)] font-mono">{facts.behindPct}%</td>
                    <td className="p-3 text-[var(--text-muted)] font-mono">{facts.p90Hours}h</td>
                  </>
                ) : (
                  <td colSpan={3} className="p-3 text-[var(--text-muted)] text-xs">
                    low sample
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="mt-8 flex flex-wrap justify-center gap-3">
        <Link
          to="/compare"
          className="bg-signal text-on-accent font-bold px-6 py-3 rounded-lg no-underline hover:opacity-90"
        >
          Compare carriers on a lane
        </Link>
        <Link
          to="/mcp"
          className="border border-border px-6 py-3 rounded-lg no-underline hover:border-signal"
        >
          Use over MCP
        </Link>
      </div>
      <p className="text-sm text-[var(--text-muted)] mt-6 mx-auto max-w-[62ch] text-center leading-relaxed">
        This list is exactly the carriers with a live schedule connector today. No thin doorway
        pages for lines we don't actually cover. It grows as new connectors go live.
      </p>

      <Faq id="carriers" items={HUB_FAQ} heading="Ocean carrier schedules: FAQ" />
    </main>
  )
}

export default CarriersHubPage
